import HealthArchiveStatus from '../components/cocohome/HealthArchiveStatus';
import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { api, ApiError } from '../lib/api';
import { useLang, useT } from '../i18n';
import type { CocoHomeDevice, CocoHomePairingCode } from '../types/cocohome';

interface PairingView {
  response: CocoHomePairingCode;
  deviceName: string;
  userName: string;
}

export default function CocoHome() {
  const t = useT();
  const { lang } = useLang();
  const [devices, setDevices] = useState<CocoHomeDevice[]>([]);
  const [devicesLoading, setDevicesLoading] = useState(true);
  const [devicesError, setDevicesError] = useState<string | null>(null);
  const [deviceName, setDeviceName] = useState('');
  const [userName, setUserName] = useState('');
  const [pairing, setPairing] = useState<PairingView | null>(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const copyResetTimer = useRef<number | null>(null);
  const resultHeadingRef = useRef<HTMLHeadingElement | null>(null);

  const loadDevices = useCallback(async (signal?: AbortSignal) => {
    setDevicesLoading(true);
    setDevicesError(null);
    try {
      const response = await api.cocoHome.devices(signal);
      setDevices(response.items);
    } catch (caught) {
      if (caught instanceof DOMException && caught.name === 'AbortError') return;
      const detail = caught instanceof Error ? caught.message : t('cocohome.devicesError');
      setDevicesError(t('cocohome.devicesErrorWithDetail', { error: detail }));
    } finally {
      if (!signal?.aborted) setDevicesLoading(false);
    }
  }, [t]);

  useEffect(() => {
    const controller = new AbortController();
    void loadDevices(controller.signal);
    return () => controller.abort();
  }, [loadDevices]);

  useEffect(() => {
    if (!pairing) return;
    setNow(Date.now());
    resultHeadingRef.current?.focus();
    const timer = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, [pairing]);

  useEffect(() => () => {
    if (copyResetTimer.current !== null) window.clearTimeout(copyResetTimer.current);
  }, []);

  const expiresAtMs = pairing ? Date.parse(pairing.response.expires_at) : 0;
  const remainingMs = Number.isFinite(expiresAtMs) ? Math.max(0, expiresAtMs - now) : 0;
  const expired = pairing !== null && remainingMs === 0;

  async function handleCreate(event: FormEvent) {
    event.preventDefault();
    const trimmedDeviceName = deviceName.trim();
    const trimmedUserName = userName.trim();
    if (creating || !trimmedDeviceName || !trimmedUserName) return;

    setCreating(true);
    setError(null);
    setCopied(false);
    try {
      const response = await api.cocoHome.createPairing({
        device_name: trimmedDeviceName,
        user_name: trimmedUserName,
      });
      setPairing({
        response,
        deviceName: response.device_name ?? trimmedDeviceName,
        userName: response.user_name ?? trimmedUserName,
      });
    } catch (caught) {
      const detail = caught instanceof Error ? caught.message : t('cocohome.errorGeneric');
      setError(
        caught instanceof ApiError && caught.status === 429
          ? t('cocohome.errorRateLimited')
          : t('cocohome.errorWithDetail', { error: detail }),
      );
    } finally {
      setCreating(false);
    }
  }

  async function handleCopy() {
    if (!pairing || expired) return;
    try {
      await copyText(pairing.response.pairing_code);
      setCopied(true);
      if (copyResetTimer.current !== null) window.clearTimeout(copyResetTimer.current);
      copyResetTimer.current = window.setTimeout(() => setCopied(false), 2_000);
    } catch {
      setError(t('cocohome.copyError'));
    }
  }

  function handleCreateAnother() {
    setPairing(null);
    setDeviceName('');
    setUserName('');
    setError(null);
    setCopied(false);
    void loadDevices();
  }

  return (
    <section className="flex flex-col gap-8">
      <header className="max-w-3xl space-y-2">
        <p className="font-mono text-xs uppercase tracking-[0.22em] text-coffee/70">
          {t('cocohome.kicker')}
        </p>
        <h2 className="text-3xl font-semibold text-cocoa">{t('cocohome.title')}</h2>
        <p className="text-sm leading-relaxed text-coffee">{t('cocohome.subtitle')}</p>
      </header>

      <HealthArchiveStatus devices={devices} />

      <section
        aria-labelledby="cocohome-devices-heading"
        className="rounded-2xl border border-neutral-200 bg-white p-5 shadow-soft md:p-6"
      >
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h3 id="cocohome-devices-heading" className="font-mono text-sm text-cocoa">
                {t('cocohome.devicesTitle')}
              </h3>
              {!devicesLoading && !devicesError && (
                <span className="rounded-full bg-neutral-100 px-2 py-0.5 font-mono text-[10px] text-coffee">
                  {t('cocohome.devicesCount', { count: devices.length })}
                </span>
              )}
            </div>
            <p className="mt-1 max-w-2xl text-sm leading-relaxed text-coffee/80">
              {t('cocohome.devicesHint')}
            </p>
          </div>
          <button
            type="button"
            disabled={devicesLoading}
            onClick={() => void loadDevices()}
            className="self-start rounded-full border border-neutral-200 px-3 py-1.5 font-mono text-xs text-cocoa transition hover:-translate-y-0.5 hover:border-tan hover:bg-tan/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-950/15 disabled:translate-y-0 disabled:cursor-wait disabled:opacity-50"
          >
            {devicesLoading ? t('cocohome.devicesRefreshing') : t('cocohome.devicesRefresh')}
          </button>
        </div>

        {devicesLoading && devices.length === 0 && (
          <p role="status" className="mt-5 font-mono text-xs text-coffee/70">
            {t('cocohome.devicesLoading')}
          </p>
        )}

        {devicesError && (
          <p role="alert" className="mt-5 rounded-xl border border-cocoa/15 bg-cocoa/5 px-4 py-3 font-mono text-xs text-cocoa">
            {devicesError}
          </p>
        )}

        {!devicesLoading && !devicesError && devices.length === 0 && (
          <p className="mt-5 rounded-xl bg-neutral-50 px-4 py-6 text-center text-sm text-coffee/75">
            {t('cocohome.devicesEmpty')}
          </p>
        )}

        {devices.length > 0 && (
          <ul className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {devices.map((device) => (
              <li
                key={device.device_id}
                className="rounded-xl border border-neutral-200 bg-neutral-50/70 p-4"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="break-words font-semibold text-cocoa">{device.device_name}</p>
                    <p className="mt-1 break-words text-xs text-coffee/75">
                      {t('cocohome.userLabel')}: {device.user_name ?? t('cocohome.assignmentUnavailable')}
                    </p>
                  </div>
                  <span
                    className={
                      device.status === 'active'
                        ? 'shrink-0 rounded-full bg-emerald-100 px-2.5 py-1 font-mono text-[10px] text-emerald-800'
                        : 'shrink-0 rounded-full bg-neutral-200 px-2.5 py-1 font-mono text-[10px] text-neutral-600'
                    }
                  >
                    {t(`cocohome.deviceStatus.${device.status}`)}
                  </span>
                </div>

                <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3 text-xs">
                  <div>
                    <dt className="font-mono text-[10px] uppercase tracking-wider text-coffee/60">
                      {t('cocohome.deviceTypeLabel')}
                    </dt>
                    <dd className="mt-1 text-coffee">
                      {t(`cocohome.deviceType.${device.device_type}`)}
                    </dd>
                  </div>
                  <div>
                    <dt className="font-mono text-[10px] uppercase tracking-wider text-coffee/60">
                      {t('cocohome.pairedAtLabel')}
                    </dt>
                    <dd className="mt-1 text-coffee">{formatDeviceDate(device.paired_at, lang)}</dd>
                  </div>
                  <div className="col-span-2">
                    <dt className="font-mono text-[10px] uppercase tracking-wider text-coffee/60">
                      {t('cocohome.lastUsedAtLabel')}
                    </dt>
                    <dd className="mt-1 text-coffee">{formatDeviceDate(device.last_used_at, lang)}</dd>
                  </div>
                </dl>
              </li>
            ))}
          </ul>
        )}
      </section>

      {!pairing ? (
        <form
          onSubmit={handleCreate}
          className="flex max-w-3xl flex-col gap-5 rounded-2xl border border-neutral-200 bg-white p-5 shadow-soft transition-shadow duration-200 hover:shadow-float md:p-6"
        >
          <div>
            <h3 className="font-mono text-sm text-cocoa">{t('cocohome.formTitle')}</h3>
            <p className="mt-1 text-sm leading-relaxed text-coffee/80">{t('cocohome.formHint')}</p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <label className="flex flex-col gap-1.5">
              <span className="text-[10px] font-mono uppercase tracking-wider text-coffee/80">
                {t('cocohome.deviceNameLabel')}
              </span>
              <input
                autoFocus
                required
                maxLength={80}
                autoComplete="off"
                spellCheck={false}
                value={deviceName}
                onChange={(event) => setDeviceName(event.target.value)}
                placeholder={t('cocohome.deviceNamePlaceholder')}
                className="rounded-xl border border-neutral-200 bg-white px-3 py-2.5 text-sm text-cocoa shadow-sm placeholder:text-neutral-400 transition focus:border-neutral-900 focus:outline-none focus:ring-2 focus:ring-neutral-950/10"
              />
            </label>

            <label className="flex flex-col gap-1.5">
              <span className="text-[10px] font-mono uppercase tracking-wider text-coffee/80">
                {t('cocohome.userNameLabel')}
              </span>
              <input
                required
                maxLength={80}
                autoComplete="name"
                value={userName}
                onChange={(event) => setUserName(event.target.value)}
                placeholder={t('cocohome.userNamePlaceholder')}
                className="rounded-xl border border-neutral-200 bg-white px-3 py-2.5 text-sm text-cocoa shadow-sm placeholder:text-neutral-400 transition focus:border-neutral-900 focus:outline-none focus:ring-2 focus:ring-neutral-950/10"
              />
            </label>
          </div>

          <p className="rounded-xl border border-tan/25 bg-tan/10 px-4 py-3 text-xs leading-relaxed text-coffee">
            {t('cocohome.privacyHint')}
          </p>

          {error && (
            <p role="alert" className="font-mono text-xs italic text-cocoa">
              {error}
            </p>
          )}

          <div>
            <button
              type="submit"
              disabled={creating || !deviceName.trim() || !userName.trim()}
              className="rounded-full bg-tan px-5 py-2.5 font-mono text-sm text-neutral-100 shadow-soft transition hover:-translate-y-0.5 hover:bg-neutral-800 hover:shadow-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-950/15 disabled:translate-y-0 disabled:cursor-not-allowed disabled:opacity-50 disabled:shadow-none"
            >
              {creating ? t('cocohome.generating') : t('cocohome.generate')}
            </button>
          </div>
        </form>
      ) : (
        <div className="grid gap-6 rounded-2xl border border-neutral-200 bg-white p-5 shadow-card md:grid-cols-[minmax(0,17rem)_minmax(0,1fr)] md:p-7">
          <div className="relative flex min-h-[17rem] items-center justify-center overflow-hidden rounded-2xl border border-neutral-200 bg-neutral-50 p-4">
            <div className={expired ? 'opacity-15 grayscale' : ''}>
              <QRCodeSVG
                value={pairing.response.pairing_uri}
                size={240}
                level="M"
                marginSize={4}
                bgColor="#ffffff"
                fgColor="#211a16"
                title={t('cocohome.qrAlt')}
                role="img"
                aria-hidden={expired || undefined}
                className="h-auto w-full max-w-[15rem]"
              />
            </div>
            {expired && (
              <div className="absolute inset-0 flex items-center justify-center p-6 text-center">
                <span className="rounded-full bg-cocoa px-4 py-2 font-mono text-xs text-white shadow-soft">
                  {t('cocohome.expiredBadge')}
                </span>
              </div>
            )}
          </div>

          <div className="flex min-w-0 flex-col justify-center gap-5">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.18em] text-coffee/65">
                {t('cocohome.resultKicker')}
              </p>
              <h3
                ref={resultHeadingRef}
                tabIndex={-1}
                className="mt-1 text-2xl font-semibold text-cocoa focus:outline-none"
              >
                {t(expired ? 'cocohome.expiredTitle' : 'cocohome.resultTitle')}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-coffee">
                {t(expired ? 'cocohome.expiredHint' : 'cocohome.scanHint')}
              </p>
            </div>

            <dl className="grid gap-3 rounded-xl bg-neutral-50 p-4 text-sm sm:grid-cols-2">
              <div>
                <dt className="font-mono text-[10px] uppercase tracking-wider text-coffee/65">
                  {t('cocohome.deviceLabel')}
                </dt>
                <dd className="mt-1 break-words text-cocoa">{pairing.deviceName}</dd>
              </div>
              <div>
                <dt className="font-mono text-[10px] uppercase tracking-wider text-coffee/65">
                  {t('cocohome.userLabel')}
                </dt>
                <dd className="mt-1 break-words text-cocoa">{pairing.userName}</dd>
              </div>
            </dl>

            <span className="sr-only" aria-live="polite">
              {expired ? t('cocohome.expiredTitle') : ''}
            </span>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
              <span className={expired ? 'font-mono text-cocoa' : 'font-mono text-coffee'}>
                {expired
                  ? t('cocohome.expired')
                  : t('cocohome.expiresIn', { time: formatRemaining(remainingMs) })}
              </span>
              {!expired && (
                <span className="text-coffee/65">
                  {t('cocohome.expiresAt', {
                    time: new Date(pairing.response.expires_at).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                    }),
                  })}
                </span>
              )}
            </div>

            <div className="rounded-xl border border-neutral-200 p-4">
              <p className="font-mono text-[10px] uppercase tracking-wider text-coffee/65">
                {t('cocohome.manualCodeLabel')}
              </p>
              <div className="mt-2 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <code className="select-all break-all text-lg font-semibold tracking-[0.12em] text-cocoa">
                  {pairing.response.pairing_code}
                </code>
                <button
                  type="button"
                  disabled={expired}
                  onClick={() => void handleCopy()}
                  className="self-start rounded-full border border-neutral-200 px-3 py-1.5 font-mono text-xs text-cocoa transition hover:-translate-y-0.5 hover:border-tan hover:bg-tan/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-950/15 disabled:translate-y-0 disabled:cursor-not-allowed disabled:opacity-45 sm:self-auto"
                >
                  {copied ? t('cocohome.copied') : t('cocohome.copy')}
                </button>
              </div>
            </div>

            {error && (
              <p role="alert" className="font-mono text-xs italic text-cocoa">
                {error}
              </p>
            )}

            <div>
              <button
                type="button"
                onClick={handleCreateAnother}
                className="rounded-full bg-neutral-900 px-5 py-2.5 font-mono text-sm text-white shadow-soft transition hover:-translate-y-0.5 hover:bg-tan hover:shadow-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-950/15"
              >
                {t('cocohome.createAnother')}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function formatRemaining(milliseconds: number): string {
  const totalSeconds = Math.max(0, Math.ceil(milliseconds / 1_000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

function formatDeviceDate(value: string, lang: 'en' | 'zh'): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat(lang === 'zh' ? 'zh-CN' : 'en', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

async function copyText(value: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(value);
      return;
    } catch {
      // Fall through to a selection-based copy for older/restricted browsers.
    }
  }

  const input = document.createElement('textarea');
  input.value = value;
  input.setAttribute('readonly', '');
  input.style.position = 'fixed';
  input.style.opacity = '0';
  document.body.appendChild(input);
  input.select();
  const copied = document.execCommand('copy');
  input.remove();
  if (!copied) throw new Error('copy failed');
}
